import { isFrontDeviceId, isRearDeviceId } from '../../../helpers/devicePairing';
import { isEventDeviceRetrieved } from './raceRetrievalCounts';
import type { EventDeviceRow } from '../types';

export type DistributionMasterRow = {
  /** Empty for a standalone rear device with no athlete assignment. */
  athleteId: string;
  athleteName: string;
  athleteRaceNo: string;
  rearDeviceId: string;
  /** Empty when no front device is assigned to this athlete. */
  frontDeviceId: string;
  /** True once every device in the pair is marked collected (distributed). */
  collected: boolean;
  /** Racked state per device, so the UI can turn each device id green independently
   * as soon as it verifies — not just once the whole pair is done. */
  rearRacked: boolean;
  frontRacked: boolean;
  /** True once every device in the pair is racked (rear, and front if assigned). */
  racked: boolean;
  collectedAt?: number;
  collectedBy?: string;
  rackedAt?: number;
  rackedBy?: string;
};

function toRow(
  athleteId: string,
  rearRow: EventDeviceRow,
  frontRow: EventDeviceRow | undefined,
): DistributionMasterRow {
  const rearDeviceId = String(rearRow.deviceId ?? '').trim();
  const frontDeviceId = frontRow ? String(frontRow.deviceId ?? '').trim() : '';
  const memberRows = frontRow ? [rearRow, frontRow] : [rearRow];
  const collected = memberRows.every((r) => Number(r.distributed ?? 0) === 1);
  const rearRacked = Number(rearRow.racked ?? 0) === 1;
  const frontRacked = frontRow ? Number(frontRow.racked ?? 0) === 1 : false;
  const racked = frontDeviceId ? rearRacked && frontRacked : rearRacked;
  return {
    athleteId,
    athleteName: String(rearRow.assignedAthleteName ?? frontRow?.assignedAthleteName ?? '').trim(),
    athleteRaceNo: String(rearRow.assignedAthleteRaceNo ?? frontRow?.assignedAthleteRaceNo ?? '').trim(),
    rearDeviceId,
    frontDeviceId,
    collected,
    rearRacked,
    frontRacked,
    racked,
    collectedAt: Number(rearRow.distributedAt ?? 0) || undefined,
    collectedBy: String(rearRow.distributedBy ?? '').trim() || undefined,
    rackedAt: Number(rearRow.rackedAt ?? 0) || undefined,
    rackedBy: String(rearRow.rackedBy ?? '').trim() || undefined,
  };
}

/**
 * One row per athlete (rear + optional paired front) for the Distribution/Racking
 * master table. Retrieved/recalled devices are excluded — retrieval is tracked as a
 * separate operation, not part of collection/racking.
 */
export function buildDistributionMasterRows(devices: EventDeviceRow[]): DistributionMasterRow[] {
  const active = devices.filter((d) => !isEventDeviceRetrieved(d));
  const byAthlete = new Map<string, { rear?: EventDeviceRow; front?: EventDeviceRow }>();
  const standaloneRears: EventDeviceRow[] = [];

  for (const row of active) {
    const did = String(row.deviceId ?? '').trim();
    if (!did) continue;
    const athleteId = String(row.assignedAthleteId ?? '').trim();
    if (!athleteId) {
      if (isRearDeviceId(did)) standaloneRears.push(row);
      continue;
    }
    const entry = byAthlete.get(athleteId) ?? {};
    if (isRearDeviceId(did)) entry.rear = row;
    else if (isFrontDeviceId(did)) entry.front = row;
    byAthlete.set(athleteId, entry);
  }

  const rows: DistributionMasterRow[] = [];
  for (const [athleteId, pair] of byAthlete) {
    if (!pair.rear) continue;
    rows.push(toRow(athleteId, pair.rear, pair.front));
  }
  for (const rear of standaloneRears) {
    rows.push(toRow('', rear, undefined));
  }

  rows.sort((a, b) => {
    const an = Number(a.athleteRaceNo);
    const bn = Number(b.athleteRaceNo);
    if (Number.isFinite(an) && an > 0 && Number.isFinite(bn) && bn > 0 && an !== bn) return an - bn;
    return a.rearDeviceId.localeCompare(b.rearDeviceId);
  });
  return rows;
}

/** Backend only allows athlete/device reassignment during RACKING (any race) or during
 * DISTRIBUTION for combined-mode races (see _assert_race_assignments_mutable). */
export function canEditDistributionAssignments(raceStatus: string, combinedMode: boolean): boolean {
  const status = String(raceStatus ?? '').trim().toUpperCase();
  if (status === 'RACKING') return true;
  return status === 'DISTRIBUTION' && combinedMode;
}
